import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getRoles, updateRole } from "../Apis/RoleAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { formatDate } from "../helper/DateHelper";
import usePermissions from "../hook/userPermission";

const RoleListLayer = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [roleToToggle, setRoleToToggle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [assignedManagers, setAssignedManagers] = useState([]);
  const [checkingManagers, setCheckingManagers] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fetchRoles = async () => {
    setLoading(true);
    const res = await getRoles({
      page: currentPage,
      limit: rowsPerPage,
      search: searchTerm,
    });
    if (res.status) {
      const data = res.response?.data || res.response;
      setRoles(Array.isArray(data) ? data : []);
      setTotalRecords(
        res.response?.total || (Array.isArray(data) ? data.length : 0),
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRoles();
  }, [currentPage, rowsPerPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleStatusClick = async (role) => {
    setRoleToToggle(role);
    setAssignedManagers([]);

    if (role.isActive) {
      setCheckingManagers(true);
      setShowStatusModal(true);
      const res = await getManagers({ limit: 1000 });
      if (res.status) {
        const allManagers = res.response?.data || res.response || [];
        const roleId = role._id || role.id;
        const assigned = allManagers.filter((m) => {
          const mRole = m.role?._id || m.role?.id || m.role;
          return mRole === roleId;
        });
        setAssignedManagers(assigned);
      }
      setCheckingManagers(false);
    } else {
      setShowStatusModal(true);
    }
  };

  const confirmStatusToggle = async () => {
    if (!roleToToggle) return;
    setSubmitting(true);
    const newStatus = !roleToToggle.isActive;
    const res = await updateRole(roleToToggle._id || roleToToggle.id, { ...roleToToggle, isActive: newStatus });
    if (res.status) {
      toast.success(`Status updated to ${newStatus ? "Active" : "Inactive"}`);
      fetchRoles();
      setShowStatusModal(false);
    }
    setSubmitting(false);
  };

  const filteredRoles = roles.filter((role) =>
    role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(totalRecords / rowsPerPage) || 1;

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden">
      <div
        className="card-header border-bottom py-20 px-24"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <h6 className="text-white mb-0 fw-bold" style={{ fontSize: "18px" }}>
            Roles & Permissions
          </h6>
          <div className="d-flex align-items-center gap-3">
            <div className="position-relative" style={{ minWidth: "250px" }}>
              <input
                type="text"
                className="form-control radius-8 ps-40 py-10 bg-white border-0"
                placeholder="Search Role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: "44px" }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute translate-middle-y top-50 start-0 ms-12 text-secondary-light"
                style={{ fontSize: "20px" }}
              />
            </div>
            {hasPermission("user_roles", "add") && (
              <Link
                to="/roles/add"
                className="btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "#f7e7da",
                  color: "black",
                  fontWeight: "600",
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                Add Role
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  S.No
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Role Name
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Created Date
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Status
                </th>
                <th
                  scope="col"
                  style={{ color: "black", fontWeight: "bold" }}
                  className="text-center"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-60">
                    Loading...
                  </td>
                </tr>
              ) : filteredRoles.length > 0 ? (
                filteredRoles.map((role, index) => (
                  <tr key={role._id || role.id} className="border-bottom">
                    <td className="py-20 px-24 text-primary-light">
                      {currentPage * rowsPerPage + index + 1}
                    </td>
                    <td className="py-20 px-24 text-primary-light">
                      {role.roleName}
                    </td>
                    <td className="py-20 px-24 text-secondary-light">
                      {formatDate(role.createdAt)}
                    </td>
                    <td className="py-20 px-24">
                      <span className={`badge px-24 py-4 radius-4 fw-medium text-sm ${role.isActive ? "bg-success-focus text-success-main" : "bg-danger-focus text-danger-main"}`}>
                        {role.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-20 px-24 text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        {hasPermission("User Roles", "edit") && (
                          <>
                            <button
                              onClick={() =>
                                navigate(`/roles/edit/${role._id || role.id}`)
                              }
                              className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              title="Edit"
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusClick(role)}
                              className={`${role.isActive ? 'bg-success-focus text-success-600' : 'bg-danger-focus text-danger-600'} fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0`}
                              title={role.isActive ? "Deactivate" : "Activate"}
                            >
                              <Icon icon={role.isActive ? "lucide:lock" : "lucide:unlock"} className="menu-icon" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-60 text-secondary-light"
                  >
                    <div className="d-flex flex-column align-items-center gap-2">
                      <Icon
                        icon="lucide:search-x"
                        style={{ fontSize: "48px", opacity: 0.2 }}
                      />
                      <span>No roles found matching your search.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage)}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>

      {/* Status Confirmation Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Body className="text-center p-32">
          {checkingManagers ? (
            <div className="py-40">
              <div className="spinner-border text-primary mb-16" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h6 className="text-secondary-light">Checking assigned managers...</h6>
            </div>
          ) : assignedManagers.length > 0 ? (
            <>
              <div className="d-flex justify-content-center mb-24">
                <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                  <Icon
                    icon="lucide:lock"
                    className="text-danger-600 text-xxl"
                  />
                </div>
              </div>
              <h6 className="mb-12">Cannot Deactivate Role</h6>
              <p className="text-secondary-light mb-24">
                This role "{roleToToggle?.roleName}" is currently assigned to the following {assignedManagers.length} manager(s). You must reassign their roles first.
              </p>

              <div className="text-start mb-32 p-16 border radius-12 bg-danger-focus overflow-y-auto" style={{ maxHeight: "240px" }}>
                <div className="d-flex flex-column gap-12">
                  {assignedManagers.map((m) => (
                    <div key={m._id || m.id} className="d-flex align-items-center justify-content-between p-12 bg-white radius-8 border shadow-sm">
                      <div className="d-flex align-items-center gap-12">
                        <div className="w-32-px h-32-px bg-danger-focus text-danger-main d-flex justify-content-center align-items-center rounded-circle fw-bold text-sm">
                          {m.name?.charAt(0)?.toUpperCase() || "M"}
                        </div>
                        <div>
                          <div className="fw-bold text-dark text-sm">{m.name}</div>
                          <div className="text-secondary-light text-xs">{m.email}</div>
                        </div>
                      </div>
                      {/* <span className="badge bg-danger-focus text-danger-main px-12 py-4 radius-4 text-xs">
                        {m.phoneNumber || m.mobile || "N/A"}
                      </span> */}
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-center mt-32">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-32 radius-8 fw-bold"
                  onClick={() => setShowStatusModal(false)}
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-center mb-24">
                <div className={`${roleToToggle?.isActive ? "bg-danger-focus" : "bg-success-focus"} rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px`}>
                  <Icon
                    icon={roleToToggle?.isActive ? "lucide:lock" : "lucide:unlock"}
                    className={`${roleToToggle?.isActive ? "text-danger-600" : "text-success-600"} text-xxl`}
                  />
                </div>
              </div>
              <h6>Are you sure you want to {roleToToggle?.isActive ? "Deactivate" : "Activate"} this role?</h6>
              <p className="text-secondary-light mb-0">
                This will {roleToToggle?.isActive ? "restrict" : "allow"} access for users assigned to this role.
              </p>
              <div className="d-flex justify-content-center gap-3 mt-32">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-32 radius-8 fw-bold"
                  onClick={() => setShowStatusModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn px-32 radius-8 fw-bold text-white border-0`}
                  style={{ backgroundColor: roleToToggle?.isActive ? "#ed1c24" : "#28a745" }}
                  onClick={confirmStatusToggle}
                  disabled={submitting}
                >
                  {submitting ? "Processing..." : (roleToToggle?.isActive ? "Deactivate" : "Activate")}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RoleListLayer;
